import React from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  FormField,
  InputGroup,
  majorScale,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import validateEmail from '../../utils/email-validator';
import { AuthLayout, AuthTitle } from './AuthLayout';

interface FormValues {
  email: string;
  password: string;
}

export const AuthLogin = () => {
  const { authStore } = useStores();
  const intl = useIntl();
  const history = useHistory();
  const toaster = useToast();
  const {
    register,
    errors,
    handleSubmit,
    formState,
    setError,
  } = useForm<FormValues>();

  const onSubmit = async (values: FormValues) => {
    try {
      await authStore.signIn(values.email, values.password);
      await authStore.setup();
      history.push('/auth/organisations');
    } catch (err) {
      if (err.response && err.response.status === 401)
        setError('password', { message: 'Kodeord eller email forkert' });
      else toaster.showDefaultError(err);
    }
  };

  return (
    <AuthLayout>
      <AuthTitle
        title={intl.formatMessage({ defaultMessage: 'Sign in to continue' })}
        sub={intl.formatMessage({ defaultMessage: 'Welcome back to Sails' })}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField
          label={intl.formatMessage({
            defaultMessage: 'Email',
            description: 'Form field label',
          })}
          marginBottom={majorScale(2)}
          className="medium"
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Email',
              description: 'Input placeholder',
            })}
            name="email"
            isInvalid={!!errors.email}
            validationMessage={errors.email && errors.email.message}
            ref={register({
              validate: (v: string) =>
                (!validateEmail(v) &&
                  intl.formatMessage({
                    defaultMessage: 'Please provide a valid email address',
                  })) ||
                true,
              required: intl.formatMessage({
                defaultMessage: 'Please enter your email',
              }),
            })}
          />
        </FormField>

        <FormField
          label={intl.formatMessage({
            defaultMessage: 'Password',
            description: 'Formfield label',
          })}
          marginBottom={majorScale(2)}
          className="medium"
          isInvalid={!!errors.password || !!errors.email}
          hint={errors.password && errors.password.message}
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Password',
              description: 'Input placeholder',
            })}
            type="password"
            name="password"
            isInvalid={!!errors.password}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please enter your password',
              }),
            })}
          />
        </FormField>

        <Button
          width="100%"
          primary
          marginBottom={majorScale(2)}
          isLoading={formState.isSubmitting}
          type="submit"
        >
          <FormattedMessage defaultMessage="Sign in" />
        </Button>
      </form>

      <Box
        is={Link}
        to="/auth/sign-up"
        className="a"
        padding={majorScale(1)}
        display="block"
        textAlign="center"
      >
        <FormattedMessage defaultMessage="Create a new account" />
      </Box>

      <Box
        is={Link}
        to="/auth/forgot"
        className="a"
        padding={majorScale(1)}
        display="block"
        textAlign="center"
      >
        <FormattedMessage defaultMessage="Forgot your password?" />
      </Box>
    </AuthLayout>
  );
};
